import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Container,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Fade,
  Grid,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Badge,
  Lock,
  Visibility,
  VisibilityOff,
  Business,
  LocationOn,
  PersonAdd,
  Login,
  CheckCircle,
  Shield,
  Security,
  Celebration,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../../utils/api";
import { getUserId } from "../../../utils/api";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as Yup from "yup";
import Cookies from "js-cookie";
import RowadLogo from "../../RowadLogo";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LoadingButton from "@mui/lab/LoadingButton";
import EnhancedHeader from "../../EnhancedHeader";

const MySwal = withReactContent(Swal);

function EnhancedSignUp({ update }) {
  const [sites, setSites] = useState([]);
  const [LoadingAPI, setLoadingAPI] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const steps = update 
    ? ["Personal Information", "Work Details", "Security"]
    : ["Personal Information", "Work Details", "Security & Verification"];

  const initialValues = {
    new_name: "",
    emailaddress: "",
    new_phone: "",
    token: "",
    new_rmecode: "",
    new_password: "",
    Confirmnew_password: "",
    Department: { name: "", id: "" },
    Site: { name: "", id: "" },
    componentUpdate: false,
  };

  const validationSchemaUpdate = Yup.object().shape({
    new_password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9])\S{8,}$/,
        "Password must include uppercase, lowercase, number, and special character (no spaces allowed)"
      )
      .required("Password is required"),
    Confirmnew_password: Yup.string().oneOf(
      [Yup.ref("new_password"), null],
      "Passwords must match"
    ),
    new_rmecode: Yup.string()
      .required("Employee code is required")
      .matches(/^[0-9]+$/, "Must be a number"),
    new_phone: Yup.string()
      .matches(/^[+]?[0-9]+$/, "Must be a valid phone number")
      .min(5, "Phone number too short"),
  });

  const validationSchema = Yup.object().shape({
    componentUpdate: Yup.boolean(),
    emailaddress: Yup.string().when("componentUpdate", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required("Email is required")
          .matches(
            /^[a-zA-Z0-9._%+-]+@(rowad-rme|rme-fitout|rme-g|driller-eg|rowad-rme\.net|g-buildings|rowad-investment|tetraes|orascom)\.(com|net)$/,
            "Email must be from an authorized domain"
          )
          .test("uniqueEmail", "Email already exists", async function (value) {
            try {
              const response = await axios.post(
                `${process.env.REACT_APP_Back_URL}/userst/checkemail`,
                { email: value }
              );
              return response.data === 0;
            } catch (error) {
              return false;
            }
          }),
    }),
    new_password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9])\S{8,}$/,
        "Password must include uppercase, lowercase, number, and special character (no spaces allowed)"
      )
      .required("Password is required"),
    Confirmnew_password: Yup.string().oneOf(
      [Yup.ref("new_password"), null],
      "Passwords must match"
    ),
    new_rmecode: Yup.string()
      .required("Employee code is required")
      .matches(/^[0-9]+$/, "Must be a number"),
    new_phone: Yup.string()
      .matches(/^[+]?[0-9]+$/, "Must be a valid phone number")
      .min(5, "Phone number too short"),
  });

  // Helper function to retry failed requests
  const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await axios.get(url, { timeout: 10000 }); // 10 second timeout
        return response;
      } catch (error) {
        if (i === retries - 1) throw error; // Throw on last retry
        console.warn(`Retry ${i + 1}/${retries} for ${url}`);
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1))); // Exponential backoff
      }
    }
  };

  const RetrieveData = async () => {
    let deptData = [];
    let siteData = [];

    // Fetch departments with retry
    try {
      const deptResp = await fetchWithRetry(`${process.env.REACT_APP_Back_URL}/tickets/departments`);
      deptData = deptResp.data
        .sort((a, b) => a.new_name.toUpperCase().localeCompare(b.new_name.toUpperCase()))
        .map((row) => ({ id: row.new_departmentsid, name: row.new_name }));
      setDepartments(deptData);
    } catch (error) {
      console.error("Error fetching departments:", error);
      MySwal.fire({
        icon: "warning",
        title: "Unable to Load Departments",
        text: "There was an issue loading departments. Please refresh the page or contact support if the issue persists.",
        confirmButtonColor: "#B41736",
      });
    }

    // Fetch sites with retry (independent of departments)
    try {
      const siteResp = await fetchWithRetry(`${process.env.REACT_APP_Back_URL}/tickets/sites`);
      siteData = siteResp.data
        .sort((a, b) => a.new_name.toUpperCase().localeCompare(b.new_name.toUpperCase()))
        .map((row) => ({ id: row.new_siteid, name: row.new_name }));
      setSites(siteData);
    } catch (error) {
      console.error("Error fetching sites:", error);
      MySwal.fire({
        icon: "warning",
        title: "Unable to Load Sites",
        text: "There was an issue loading sites. Please refresh the page or contact support if the issue persists.",
        confirmButtonColor: "#B41736",
      });
    }

    // Return success status
    return { departments: deptData, sites: siteData };
  };

  useEffect(() => {
    RetrieveData().then(async () => {
      if (update) {
        try {
          // Retry logic for user data as well
          let response;
          for (let i = 0; i < 3; i++) {
            try {
              response = await api.post(
                '/userst/single',
                { id: getUserId() }
              );
              break; // Success, exit retry loop
            } catch (error) {
              if (i === 2) throw error; // Last retry failed
              console.warn(`Retry ${i + 1}/3 for user data`);
              await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
          }

          const {
            _new_department_value,
            _new_site_value,
            new_password,
            ...rest
          } = response.data;
          
          formik.setValues({
            new_password,
            Confirmnew_password: new_password,
            Department: { id: _new_department_value, name: "" },
            Site: { id: _new_site_value, name: "" },
            ...rest,
          });
        } catch (error) {
          console.error("Error fetching user data:", error);
          MySwal.fire({
            icon: "error",
            title: "Unable to Load Account Data",
            text: "There was an issue loading your account information. Please refresh the page or try again later.",
            confirmButtonColor: "#B41736",
          });
        }
      }
    });
  }, []);

  const formik = useFormik({
    initialValues,
    onSubmit: async (value) => {
      setLoadingAPI(true);
      let recaptchaToken = '';
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('signup');
      }
      const { Site, Department, componentUpdate, Confirmnew_password, emailaddress, token, ...rest } = value;

      if (update) {
        api
          .patch('/userst/update-account', {
            id: getUserId(),
            "new_Site@odata.bind": `/new_sites(${Site.id})`,
            "new_Department@odata.bind": `/new_departmentses(${Department.id})`,
            ...rest,
          })
          .then((resp) => {
            if (resp.status === 200) {
              MySwal.fire({
                icon: "success",
                title: "Account Updated Successfully! ✅",
                showConfirmButton: false,
                timer: 1500,
                background: "#51647C",
                color: "white",
              }).then(() => setTimeout(() => navigate("/enhanced-tickets"), 1500));
            }
          })
          .catch((error) => {
            MySwal.fire({
              icon: "error",
              title: "Update Failed",
              text: error.message,
            });
            setLoadingAPI(false);
          });
      } else {
        axios
          .post(`${process.env.REACT_APP_Back_URL}/userst`, {
            user: {
              emailaddress,
              token: recaptchaToken,
              new_url: `${window.location.protocol}//${window.location.host}/activate/`,
              "new_Site@odata.bind": `/new_sites(${Site.id})`,
              "new_Department@odata.bind": `/new_departmentses(${Department.id})`,
              ...rest,
            },
          })
          .then((resp) => {
            if (resp.status === 200) {
              MySwal.fire({
                icon: "success",
                title: "Account Created Successfully! 🎉",
                text: "Please check your email to activate your account",
                showConfirmButton: false,
                timer: 2000,
                background: "#51647C",
                color: "white",
              }).then(() => setTimeout(() => navigate("/sign-in"), 1500));
            }
          })
          .catch((error) => {
            MySwal.fire({
              icon: "error",
              title: "Registration Failed",
              text: error.message,
            });
            setLoadingAPI(false);
          });
      }
    },
    validationSchema: update ? validationSchemaUpdate : validationSchema,
  });

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              id="new_name"
              name="new_name"
              label="Full Name"
              placeholder="Enter your full name"
              {...formik.getFieldProps("new_name")}
              error={formik.touched.new_name && Boolean(formik.errors.new_name)}
              helperText={formik.touched.new_name && formik.errors.new_name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />

            <TextField
              fullWidth
              id="emailaddress"
              name="emailaddress"
              type="email"
              label="Company Email"
              placeholder="your.email@company.com"
              disabled={update}
              {...formik.getFieldProps("emailaddress")}
              error={formik.touched.emailaddress && Boolean(formik.errors.emailaddress)}
              helperText={formik.touched.emailaddress && formik.errors.emailaddress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />

            <TextField
              fullWidth
              id="new_phone"
              name="new_phone"
              label="Mobile Number"
              placeholder="+20xxxxxxxxxx"
              {...formik.getFieldProps("new_phone")}
              error={formik.touched.new_phone && Boolean(formik.errors.new_phone)}
              helperText={formik.touched.new_phone && formik.errors.new_phone}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />

            <TextField
              fullWidth
              id="new_rmecode"
              name="new_rmecode"
              label="Employee Code"
              placeholder="Enter your employee code"
              {...formik.getFieldProps("new_rmecode")}
              error={formik.touched.new_rmecode && Boolean(formik.errors.new_rmecode)}
              helperText={formik.touched.new_rmecode && formik.errors.new_rmecode}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Autocomplete
              fullWidth
              options={departments}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                formik.values.Department?.name === ""
                  ? departments.find((dept) => dept.id === formik.values.Department?.id) || { id: "", name: "" }
                  : formik.values.Department
              }
              onChange={(e, value) => {
                formik.setFieldValue("Department", value || { id: "", name: "" });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  required
                  error={formik.touched.Department?.id && Boolean(formik.errors.Department?.id)}
                  helperText={formik.touched.Department?.id && formik.errors.Department?.id}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              fullWidth
              options={sites}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                formik.values.Site?.name === ""
                  ? sites.find((site) => site.id === formik.values.Site?.id) || { id: "", name: "" }
                  : formik.values.Site
              }
              onChange={(e, value) => {
                formik.setFieldValue("Site", value || { id: "", name: "" });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Site"
                  required
                  error={formik.touched.Site?.id && Boolean(formik.errors.Site?.id)}
                  helperText={formik.touched.Site?.id && formik.errors.Site?.id}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationOn color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                    },
                  }}
                />
              )}
            />
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              id="new_password"
              name="new_password"
              type={showPassword ? "text" : "password"}
              label="Password"
              placeholder="Enter a strong password"
              {...formik.getFieldProps("new_password")}
              error={formik.touched.new_password && Boolean(formik.errors.new_password)}
              helperText={formik.touched.new_password && formik.errors.new_password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />

            <TextField
              fullWidth
              id="Confirmnew_password"
              name="Confirmnew_password"
              type={showConfirmPassword ? "text" : "password"}
              label="Confirm Password"
              placeholder="Re-enter your password"
              {...formik.getFieldProps("Confirmnew_password")}
              error={formik.touched.Confirmnew_password && Boolean(formik.errors.Confirmnew_password)}
              helperText={formik.touched.Confirmnew_password && formik.errors.Confirmnew_password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CheckCircle color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" },
                },
              }}
            />

          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {update && <EnhancedHeader />}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#51647C",
          position: "relative",
          overflow: "hidden",
          py: 4,
        }}
      >
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 6s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 8s ease-in-out infinite",
        }}
      />

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-20px) translateX(20px); }
          }
        `}
      </style>

      <Container maxWidth={update ? "lg" : "md"}>
        <Fade in={isVisible} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              {!update && (
                <Box sx={{ mb: 3 }}>
                  <RowadLogo />
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                {update ? <Person sx={{ fontSize: 32, mr: 1, color: "#51647C" }} /> : <Celebration sx={{ fontSize: 32, mr: 1, color: "#51647C" }} />}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#51647C",
                  }}
                >
                  {update ? "My Account" : "Create Account"}
                </Typography>
              </Box>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {update ? "Update your account information" : "Join our support system today"}
              </Typography>

              <Card sx={{ bgcolor: update ? "error.light" : "info.light", color: "white", mb: 2 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <Security sx={{ fontSize: 20 }} />
                    <Typography variant="body2">
                      {update ? "Secure account update" : "Your data is secure with us"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {!update && (
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            <form onSubmit={formik.handleSubmit}>
              {update ? (
                <Grid container spacing={3}>
                  {/* Left Column - Personal & Contact Info */}
                  <Grid item xs={12} md={6}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        p: 3, 
                        height: '100%',
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                        👤 Personal Information
                      </Typography>
                      {renderStepContent(0)}
                    </Card>
                  </Grid>
                  
                  {/* Right Column - Work Details & Security */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      <Card 
                        elevation={0}
                        sx={{ 
                          p: 3,
                          bgcolor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                          💼 Work Details
                        </Typography>
                        {renderStepContent(1)}
                      </Card>
                      
                      <Card 
                        elevation={0}
                        sx={{ 
                          p: 3,
                          bgcolor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                          🔒 Security
                        </Typography>
                        {renderStepContent(2)}
                      </Card>
                    </Stack>
                  </Grid>
                </Grid>
              ) : (
                renderStepContent(activeStep)
              )}

              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                {!update && activeStep > 0 && (
                  <Button 
                    onClick={() => setActiveStep((prev) => prev - 1)} 
                    variant="outlined"
                    sx={{ minWidth: 80, px: 2 }}
                  >
                    Back
                  </Button>
                )}

                <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
                  {!update && activeStep < steps.length - 1 ? (
                    <Button 
                      onClick={() => setActiveStep((prev) => prev + 1)} 
                      variant="contained"
                      sx={{
                        background: "#B41736",
                        "&:hover": {
                          background: "#8f1129",
                        },
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <LoadingButton
                      fullWidth={update}
                      size="large"
                      variant="contained"
                      loading={LoadingAPI}
                      type="submit"
                      startIcon={update ? <CheckCircle /> : <PersonAdd />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        background: "#B41736",
                        boxShadow: "0 4px 15px rgba(180, 23, 54, 0.4)",
                        "&:hover": {
                          background: "#8f1129",
                          boxShadow: "0 6px 20px rgba(180, 23, 54, 0.6)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {update ? "Update Account" : "Create Account"}
                    </LoadingButton>
                  )}
                </Box>
              </Box>

              {!update && (
                <>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>

                  <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    startIcon={<Login />}
                    onClick={() => navigate("/sign-in")}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 15px rgba(79, 172, 254, 0.3)",
                      },
                    }}
                  >
                    Already have an account? Sign In
                  </Button>
                </>
              )}
            </form>

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} ROWAD RME. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
    </>
  );
}

export default EnhancedSignUp;
